import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Button } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SaveIcon from "@material-ui/icons/Save";
import VisibilityIcon from "@material-ui/icons/Visibility";
import axios from "axios";
import lodash from "lodash";
import Input from "../../components/Control/Input";
import useConfirm from "../../components/Control/useConfirm";
import { Wrapper } from "../../components/Control/Flexbox";
import MasterECNModal from "../MasterFormat/MasterECNModal";
import SpecialProcessPart from "./SpecialProcessPart";
import cpGrid from "../AddEditDuplicatePart/ControlPLan/createCPGrid.module.css";

const CreateSpecialProcess = () => {
  const { specialProcessId, id } = useParams();
  const history = useHistory();
  const [render, setRender] = useState(false);
  const [useEffectRender, setUseEffectRender] = useState(false);
  const [specialProcessData, setSpecialProcessData] = useState({
    processes: [],
  });
  const [prevSpecialProcessData, setPrevSpecialProcessData] = useState(null);
  const [isECNModal, setIsECNModal] = useState(false);

  const [DialogUpdate, confirmUpdate] = useConfirm(
    "UPDATE SPECIAL PROCESS",
    "Revision no. already exists! Are you sure you want to overwrite?",
  );
  const [DialogClose, confirmClose] = useConfirm(
    "CLOSE",
    "Are you sure you want to leave this page?",
  );
  const [DialogSave, confirmSave] = useConfirm(
    "SAVE",
    "Are you sure you want to save?",
  );

  useEffect(() => {
    axios
      .get(`/api/specialProcess/revision/${id}`)
      .then((res) => {
        if (res.data) {
          const data = res.data;
          // a special process always holds exactly one process
          if (!data.processes?.length) {
            data.processes = [
              {
                s_no: 1,
                process_no: data.special_process_id?.process_no || "",
                process_name: data.special_process_id?.process_name || "",
                specs: [],
              },
            ];
          }
          setSpecialProcessData(data);
          setPrevSpecialProcessData(lodash.cloneDeep(data));
        }
      })
      .catch((err) => console.log(err));
  }, [id, useEffectRender]);

  const handleSpecialProcessInputs = (e) => {
    specialProcessData[e.target.name] = e.target.value;
    setRender((prev) => !prev);
  };

  const handleChangeProcess = (e, index) => {
    specialProcessData.processes[index][e.target.name] = e.target.value;
    setRender((prev) => !prev);
  };

  // special_process_id comes populated from the api, it has to go back as an id
  const normalizeRefs = (data) => {
    data.special_process_id = specialProcessId;
    data.category_id = data.category_id?._id || data.category_id;
    return data;
  };

  // the process name / number are also shown outside this page (process list and
  // revision list), so the parent special process is kept in sync on every save
  const syncProcessDetails = async () => {
    const process = specialProcessData.processes?.[0];
    if (!process) return;
    await axios
      .put(`/api/specialProcess/process/${specialProcessId}`, {
        process_name: process.process_name,
        process_no: process.process_no,
      })
      .catch((err) => console.log(err));
  };

  const handleSpecialProcessSubmit = async () => {
    const isRev = await axios
      .get(
        `/api/specialProcess/isRevNo/${specialProcessId}/${specialProcessData.revision_no}`,
      )
      .catch((err) => console.log(err));
    if (!isRev) return;

    if (isRev.data.status === "success") {
      const isUpdate = await confirmUpdate();
      if (!isUpdate) return;
      const isSave = await confirmSave();
      if (!isSave) return;
      await axios
        .put(
          `/api/specialProcess/revision/${specialProcessData._id}`,
          normalizeRefs(specialProcessData),
        )
        .then(async () => {
          await syncProcessDetails();
          alert("Special process changes saved successfully");
          setUseEffectRender((prev) => !prev);
        })
        .catch((err) => console.log(err));
      return;
    }
    // revision number does not exist yet, the changes have to go through ecn
    setIsECNModal(true);
  };

  const handleSubmitRevision = async (revisionData) => {
    if (lodash.isEmpty(revisionData)) {
      alert("you can't create revision");
      return;
    }
    delete specialProcessData._id;
    delete specialProcessData.createdAt;
    delete specialProcessData.updatedAt;
    delete specialProcessData.__v;
    specialProcessData.processes.forEach((process) => {
      delete process._id;
      process.specs.forEach((spec) => delete spec._id);
    });
    // the revision date is not asked from the user, the day of the ecn is used
    specialProcessData.date_review = new Date();
    revisionData = Object.keys(revisionData)
      .map((key) => {
        if (!revisionData[key].tolerance_change) {
          return {
            rev_no: specialProcessData.revision_no,
            summary: revisionData[key].value,
            reason: revisionData[key].reason,
            section: revisionData[key].section,
            rev_date: specialProcessData.date_review,
          };
        }
        return undefined;
      })
      .filter((obj) => obj !== undefined);
    specialProcessData.revisionData = [
      ...(specialProcessData.revisionData || []),
      ...revisionData,
    ];
    await axios
      .put(`/api/specialProcess/setIsLatestRevisionFalse/${specialProcessId}`)
      .catch((error) => console.log(error));
    specialProcessData.isLatestRevision = true;
    await axios
      .post("/api/specialProcess/revision", normalizeRefs(specialProcessData))
      .then(async () => {
        await syncProcessDetails();
        alert("special process submitted successfully");
        history.push(`/specialProcess/revisions/${specialProcessId}`);
      })
      .catch((err) => console.log(err));
  };

  return (
    <>
      <main style={{ paddingBottom: "4rem" }}>
        <div
          className="divflex"
          style={{
            fontSize: "24px",
            justifyContent: "space-between",
          }}
        >
          EDIT SPECIAL PROCESS
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {specialProcessData._id && (
              <Button
                type="button"
                variant="contained"
                color="primary"
                size="large"
                style={{
                  backgroundColor: "#fff",
                  marginRight: "0.5rem",
                  color: "#054A29",
                }}
                startIcon={<VisibilityIcon />}
                onClick={() =>
                  history.push(`/specialProcess/view/${specialProcessData._id}`)
                }
              >
                View
              </Button>
            )}
            <Button
              type="button"
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSpecialProcessSubmit}
              style={{
                backgroundColor: "#003566",
                marginRight: "0.5rem",
              }}
              startIcon={<SaveIcon />}
            >
              Save & Continue
            </Button>
            <Button
              type="button"
              variant="contained"
              color="primary"
              size="large"
              style={{
                backgroundColor: "red",
                marginRight: "0.5rem",
              }}
              startIcon={<CloseIcon />}
              onClick={async () => {
                const ans = await confirmClose();
                if (ans) {
                  history.goBack();
                }
              }}
            >
              Close
            </Button>
          </div>
        </div>
        <div className="divflex">
          <div>
            <b>Process No. : </b>
            {specialProcessData.special_process_id?.process_no}
          </div>
          <div>
            <b>Process Name : </b>
            {specialProcessData.special_process_id?.process_name}
          </div>
        </div>
      </main>

      <div style={{ margin: "1rem", paddingBottom: "4rem" }}>
        <div className={cpGrid.cpInputGrid}>
          <Input
            type="Number"
            name="revision_no"
            placeholder="Later Revision"
            onChange={(e) => handleSpecialProcessInputs(e)}
            value={specialProcessData.revision_no}
          />
        </div>

        <Wrapper
          justify="space-between"
          style={{ borderBottom: "1px solid #c80202" }}
        >
          <p className="card-btn-para" style={{ color: "#c80202" }}>
            • ADD{" "}
            <span style={{ fontWeight: 400, color: "black" }}>
              Specifications
            </span>
          </p>
        </Wrapper>

        {specialProcessData.processes.map((process, index) => (
          <SpecialProcessPart
            key={process._id || index}
            {...{
              specialProcessData,
              process,
              index,
              handleChangeProcess,
            }}
          />
        ))}
      </div>

      <DialogUpdate isSubmitContent={true} />
      <DialogSave isSubmitContent={true} />
      <DialogClose isSubmitContent={false} />
      {isECNModal && prevSpecialProcessData && (
        <MasterECNModal
          openPopup={isECNModal}
          closeModal={() => setIsECNModal(false)}
          partData={specialProcessData}
          documentType="controlplan"
          ecnTitle="ECN - SPECIAL PROCESS CHANGES"
          partDataForRevision={prevSpecialProcessData}
          submit={handleSubmitRevision}
        />
      )}
    </>
  );
};

export default CreateSpecialProcess;
